import React, { useState, useEffect, useMemo } from 'react';
import { useSecretaryStore } from '../store/secretaryStore';
import { X, Calendar, Stethoscope, Banknote, Plus, Trash2, CheckCircle2, ChevronRight, Calculator, AlertCircle } from 'lucide-react';
import { fetchServiciosProfesional, checkoutCita, CheckoutPayload } from '../services/CalendarService';

interface ServicioOption {
  id: number;
  titulo_opcion: string;
  precio_sugerido: number;
  moneda: string;
}

interface CartItem extends ServicioOption {
  cantidad: number;
}

export function CheckoutCitaSlideOver() {
  const { isCheckoutCitaOpen, setCheckoutCitaOpen, citaCalendarioSeleccionada, addNotificacion } = useSecretaryStore();
  
  // Tasa BCV (Obtenida de Cloudflare igual que en Portal de Pacientes)
  const [tasaDolar, setTasaDolar] = useState<number>(36.5);
  const [tasaEuro, setTasaEuro] = useState<number>(39.5);

  // Estados Logísticos
  const [fecha, setFecha] = useState('');
  const [horaInicio, setHoraInicio] = useState('');
  const [horaFin, setHoraFin] = useState('');
  
  // Estados de Presupuesto
  const [serviciosDisponibles, setServiciosDisponibles] = useState<ServicioOption[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  
  // Estados de Pago
  const [isPayingNow, setIsPayingNow] = useState(false);
  const [bancoEmisor, setBancoEmisor] = useState('');
  const [referencia, setReferencia] = useState('');
  const [telefonoEmisor, setTelefonoEmisor] = useState('');
  
  const [isSaving, setIsSaving] = useState(false);

  // 1. Cargar Tasas BCV
  useEffect(() => {
    const workerUrl = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || 'https://aura-routing.aura-salud.workers.dev';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const finalUrl = (import.meta.env.DEV || isLocalhost) ? '/assets/tasas_bcv.json' : `${workerUrl}/assets/tasas_bcv.json?v=${Date.now()}`;
    fetch(finalUrl)
      .then(res => res.json())
      .then(data => {
        if (data?.USD) setTasaDolar(data.USD);
        if (data?.EUR) setTasaEuro(data.EUR);
      })
      .catch(err => {
        console.warn('Fallback activo: No se pudo obtener la tasa BCV desde el CDN', err);
      });
  }, []);

  // 2. Inicializar estado al abrir
  useEffect(() => {
    if (isCheckoutCitaOpen && citaCalendarioSeleccionada) {
      setFecha(citaCalendarioSeleccionada.fecha_hora_inicio.split('T')[0]);
      setHoraInicio(citaCalendarioSeleccionada.fecha_hora_inicio.split('T')[1].substring(0, 5));
      setHoraFin(citaCalendarioSeleccionada.fecha_hora_fin.split('T')[1].substring(0, 5));
      setIsPayingNow(false);
      setCart([]);
      setBancoEmisor('');
      setReferencia('');
      
      // Fetch servicios del doctor
      fetchServiciosProfesional(citaCalendarioSeleccionada.profesional_uuid).then(data => {
        setServiciosDisponibles(data);
        // Autoseleccionar el servicio actual si existe
        if (citaCalendarioSeleccionada.servicio_titulo) {
          const match = data.find(s => s.titulo_opcion === citaCalendarioSeleccionada.servicio_titulo);
          if (match) setCart([{ ...match, cantidad: 1 }]);
        }
      });
    }
  }, [isCheckoutCitaOpen, citaCalendarioSeleccionada]);

  // Cálculos de Totales
  const { totalUSD, totalVES } = useMemo(() => {
    let usd = 0;
    let ves = 0;
    cart.forEach(item => {
      const lineTotal = item.precio_sugerido * item.cantidad;
      if (item.moneda === 'USD') {
        usd += lineTotal;
        ves += lineTotal * tasaDolar;
      } else if (item.moneda === 'EUR') {
        // Asumimos EUR ~ USD para el total USD visual (simplificación) o se puede separar.
        usd += lineTotal * (tasaEuro / tasaDolar);
        ves += lineTotal * tasaEuro;
      } else {
        ves += lineTotal;
        usd += lineTotal / tasaDolar;
      }
    });
    return { totalUSD: usd, totalVES: ves };
  }, [cart, tasaDolar, tasaEuro]);

  // Manejadores del Carrito
  const addToCart = (srv: ServicioOption) => {
    setCart(prev => {
      const ex = prev.some(i => i.id === srv.id);
      if (ex) return prev.map(i => i.id === srv.id ? { ...i, cantidad: i.cantidad + 1 } : i);
      return [...prev, { ...srv, cantidad: 1 }];
    });
  };

  const removeFromCart = (id: number) => {
    setCart(prev => prev.filter(i => i.id !== id));
  };

  const updateQuantity = (id: number, qty: number) => {
    if (qty < 1) return removeFromCart(id);
    setCart(prev => prev.map(i => i.id === id ? { ...i, cantidad: qty } : i));
  };

  // Guardar Transacción
  const handleCheckout = async () => {
    if (!citaCalendarioSeleccionada) return;
    setIsSaving(true);
    
    const fechaInicioIso = new Date(`${fecha}T${horaInicio}:00`).toISOString();
    const fechaFinIso = new Date(`${fecha}T${horaFin}:00`).toISOString();

    const payload: CheckoutPayload = {
      cita_id: citaCalendarioSeleccionada.cita_id,
      fecha_hora_inicio: fechaInicioIso,
      fecha_hora_fin: fechaFinIso,
      servicios: cart.map(c => ({
        servicio_id: c.id,
        cantidad: c.cantidad,
        precio_unitario: c.precio_sugerido,
        moneda: c.moneda
      })),
    };

    if (isPayingNow) {
      payload.pago = {
        monto_pagado: totalVES, // Asumimos que el pago móvil es en VES, ajustar según UI final
        banco_emisor: bancoEmisor,
        referencia_6_digitos: referencia,
        telefono_emisor: telefonoEmisor || 'PRESENCIAL'
      };
    }

    const { success, message } = await checkoutCita(payload);
    setIsSaving(false);
    
    if (success) {
      setCheckoutCitaOpen(false);
    } else {
      addNotificacion(`Error en Checkout: ${message}`, 'warning');
    }
  };

  if (!isCheckoutCitaOpen || !citaCalendarioSeleccionada) return null;

  return (
    <>
      <button 
        type="button"
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm z-50 animate-in fade-in cursor-default"
        onClick={() => setCheckoutCitaOpen(false)}
      />

      <div className="absolute top-0 right-0 h-full w-full sm:w-112.5 md:w-137.5 max-w-full bg-white dark:bg-slate-900 shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200 dark:border-slate-800">
        
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
          <div>
            <h2 className="text-xl font-black text-slate-800 dark:text-slate-100 flex items-center gap-2 tracking-tight">
              <Calculator className="h-6 w-6 text-emerald-500" />
              Checkout de Cita
            </h2>
            <p className="text-sm font-medium text-slate-500 mt-0.5">
              Paciente: <span className="text-slate-700 dark:text-slate-300">{citaCalendarioSeleccionada.paciente_nombre}</span>
            </p>
          </div>
          <button 
            onClick={() => setCheckoutCitaOpen(false)}
            className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full text-slate-500 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-8">
          
          {/* 1. SECCIÓN LOGÍSTICA */}
          <section>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Calendar className="h-4 w-4" /> 1. Reprogramación (Opcional)
            </h3>
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-4 border border-slate-200 dark:border-slate-700 flex gap-4">
              <div className="flex-1">
                <label htmlFor="fecha-input" className="text-xs font-bold text-slate-500 mb-1.5 block">Fecha</label>
                <input id="fecha-input" type="date" value={fecha} onChange={e => setFecha(e.target.value)} className="w-full bg-white dark:bg-slate-800 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm outline-none focus:border-blue-500 transition-colors" />
              </div>
              <div className="w-24">
                <label htmlFor="hora-inicio-input" className="text-xs font-bold text-slate-500 mb-1.5 block">Inicio</label>
                <input id="hora-inicio-input" type="time" value={horaInicio} onChange={e => setHoraInicio(e.target.value)} className="w-full bg-white dark:bg-slate-800 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm outline-none focus:border-blue-500 transition-colors" />
              </div>
              <div className="w-24">
                <label htmlFor="hora-fin-input" className="text-xs font-bold text-slate-500 mb-1.5 block">Fin</label>
                <input id="hora-fin-input" type="time" value={horaFin} onChange={e => setHoraFin(e.target.value)} className="w-full bg-white dark:bg-slate-800 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm outline-none focus:border-blue-500 transition-colors" />
              </div>
            </div>
          </section>

          {/* 2. SECCIÓN PRESUPUESTO */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <Stethoscope className="h-4 w-4" /> 2. Presupuesto Clínico
              </h3>
            </div>
            
            <div className="flex flex-col gap-3">
              {/* Buscador Rápido de Servicios */}
              <div className="relative group">
                <select 
                  className="w-full appearance-none bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-medium py-3 px-4 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all cursor-pointer"
                  onChange={(e) => {
                    const s = serviciosDisponibles.find(x => x.id === Number(e.target.value));
                    if (s) addToCart(s);
                    e.target.value = ""; // Reset selector
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>Añadir un servicio médico...</option>
                  {serviciosDisponibles.map(s => (
                    <option key={s.id} value={s.id}>{s.titulo_opcion} - {s.precio_sugerido}{s.moneda}</option>
                  ))}
                </select>
                <Plus className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-500 pointer-events-none group-hover:scale-110 transition-transform" />
              </div>

              {/* Lista del Carrito */}
              <div className="flex flex-col gap-2 mt-2">
                {cart.length === 0 ? (
                  <div className="text-center py-6 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl text-slate-400 text-sm">
                    No se han añadido servicios a esta cita.
                  </div>
                ) : (
                  cart.map((item, index) => (
                    <div key={`${item.id}-${index}`} className="flex items-center justify-between bg-white dark:bg-slate-800 p-3 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm animate-in fade-in zoom-in-95 duration-200">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 dark:text-slate-200 truncate">{item.titulo_opcion}</p>
                        <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{item.precio_sugerido} {item.moneda}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 bg-slate-50 dark:bg-slate-900 rounded-lg p-1 border border-slate-200 dark:border-slate-700">
                          <button onClick={() => updateQuantity(item.id, item.cantidad - 1)} className="w-7 h-7 flex items-center justify-center font-bold text-slate-500 hover:text-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md">-</button>
                          <span className="w-6 text-center font-bold text-sm text-slate-700 dark:text-slate-300">{item.cantidad}</span>
                          <button onClick={() => updateQuantity(item.id, item.cantidad + 1)} className="w-7 h-7 flex items-center justify-center font-bold text-slate-500 hover:text-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md">+</button>
                        </div>
                        <button onClick={() => removeFromCart(item.id)} className="p-2 text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Totalizador */}
              {cart.length > 0 && (
                <div className="mt-4 bg-emerald-50 dark:bg-emerald-900/20 p-5 rounded-2xl border border-emerald-100 dark:border-emerald-800/50 flex flex-col gap-1 items-end relative overflow-hidden">
                  <div className="absolute -right-4 -top-4 opacity-5">
                    <Calculator className="w-32 h-32" />
                  </div>
                  <span className="text-xs font-black text-emerald-600/80 uppercase tracking-widest">Total a Pagar</span>
                  <div className="text-3xl font-black text-emerald-700 dark:text-emerald-400">
                    ${totalUSD.toFixed(2)}
                  </div>
                  <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-500 flex items-center gap-2">
                    <span>Bs. {totalVES.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <span className="text-[10px] bg-emerald-100 dark:bg-emerald-900/50 px-1.5 py-0.5 rounded-full text-emerald-700">BCV: {tasaDolar}</span>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* 3. SECCIÓN PAGO EN CALIENTE */}
          <section>
            <button 
              type="button"
              className="w-full flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded-2xl cursor-pointer transition-colors hover:bg-blue-100/50 text-left" 
              onClick={() => setIsPayingNow(!isPayingNow)}
            >
              <div className={`w-6 h-6 rounded-md flex items-center justify-center border-2 transition-colors ${isPayingNow ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'}`}>
                {isPayingNow && <CheckCircle2 className="w-4 h-4 text-white" />}
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-blue-900 dark:text-blue-100">¿Cobrar en este momento?</h4>
                <p className="text-xs text-blue-700/80 dark:text-blue-300/80">Ideal para pacientes presenciales (Pago móvil, Divisas, etc).</p>
              </div>
            </button>

            {isPayingNow && (
              <div className="mt-4 flex flex-col gap-4 animate-in fade-in slide-in-from-top-4 duration-300 p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label htmlFor="banco-emisor-input" className="text-xs font-bold text-slate-500 mb-1.5 block">Método / Banco Origen</label>
                    <input id="banco-emisor-input" type="text" placeholder="Ej: Efectivo, Pago Móvil, Punto de Venta" value={bancoEmisor} onChange={e => setBancoEmisor(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-900 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all" />
                  </div>
                  <div>
                    <label htmlFor="referencia-input" className="text-xs font-bold text-slate-500 mb-1.5 block">Referencia (Últimos 6)</label>
                    <input id="referencia-input" type="text" placeholder="123456" maxLength={6} value={referencia} onChange={e => setReferencia(e.target.value.replace(/\D/g, ''))} className="w-full bg-slate-50 dark:bg-slate-900 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all tracking-widest text-center" />
                  </div>
                  <div>
                    <label htmlFor="telefono-emisor-input" className="text-xs font-bold text-slate-500 mb-1.5 block">Teléfono (Opcional)</label>
                    <input id="telefono-emisor-input" type="text" placeholder="0414..." value={telefonoEmisor} onChange={e => setTelefonoEmisor(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-900 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium outline-none focus:border-blue-500 transition-all" />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-medium text-amber-600 bg-amber-50 p-3 rounded-xl border border-amber-200">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  Al guardar, la cita se marcará instantáneamente como confirmada y pagada.
                </div>
              </div>
            )}
          </section>

        </div>

        {/* Footer Actions */}
        <div className="p-5 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-[0_-10px_20px_rgba(0,0,0,0.02)]">
          <button
            onClick={handleCheckout}
            disabled={isSaving || (isPayingNow && (bancoEmisor.length < 2 || referencia.length < 4))}
            className="w-full py-4 font-black text-white bg-slate-900 hover:bg-black dark:bg-emerald-600 dark:hover:bg-emerald-500 rounded-2xl transition-all shadow-lg hover:shadow-xl active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            {isSaving ? (
              <span className="animate-pulse">Procesando Checkout...</span>
            ) : (
              <>
                <Banknote className="h-5 w-5 opacity-70 group-hover:scale-110 transition-transform" />
                {isPayingNow ? 'Guardar Cambios y Registrar Pago' : 'Guardar Presupuesto'}
                <ChevronRight className="h-5 w-5 opacity-50" />
              </>
            )}
          </button>
        </div>

      </div>
    </>
  );
}
